import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Truck, Send, Slash, Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ETA_OPTIONS = [15, 30, 45, 60];

/**
 * Claim Shift dialog used on the Employee schedule. Steps:
 *   1. Tech picks ETA.
 *   2. Shows SMS preview using the chosen ETA + tech name.
 *   3. Tech chooses "Send ETA Message" or "Skip".
 *   4. Calls POST /schedule/{id}/claim {eta_minutes, notify_customer}.
 */
export const ClaimShiftDialog = ({ open, appointment, technicianName, onClose, onClaimed }) => {
  const [eta, setEta] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  if (!appointment) return null;
  const firstName = (appointment.customer_name || '').split(' ')[0] || 'there';
  const previewText = `Hi ${firstName}, your NexFlow technician ${technicianName || 'your technician'} has claimed your appointment and is expected to arrive in about ${eta} minutes. Thank you for choosing NexFlow.`;

  const submit = async (notifyCustomer) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/schedule/${appointment.id}/claim`,
        { eta_minutes: eta, notify_customer: notifyCustomer },
        { withCredentials: true }
      );
      if (notifyCustomer && data?.sms_result?.sent) {
        toast.success(`Shift claimed — customer notified (ETA ${eta} min)`);
      } else if (notifyCustomer && data?.sms_result?.reason === 'sms_globally_disabled') {
        toast.warning('Shift claimed — global SMS is disabled, customer not notified.');
      } else if (notifyCustomer && data?.sms_result?.fallback) {
        toast.warning('Shift claimed — Twilio not configured, customer not notified.');
      } else if (!notifyCustomer) {
        toast.success(`Shift claimed silently (ETA ${eta} min)`);
      } else {
        toast.success('Shift claimed');
      }
      onClaimed && onClaimed(data);
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim shift');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose && onClose(); }}>
      <DialogContent data-testid="claim-shift-dialog" className="bg-[#141414] border border-[#262626] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Truck className="w-5 h-5 text-[#D4AF37]" /> Claim Shift
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            Picking an ETA marks you en-route. Choose whether to text the customer your arrival window.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* iter80 — Customer contact strip visible BEFORE claiming, so the tech can call to
              confirm details without backing out of the dialog. */}
          {(appointment.customer_name || appointment.customer_phone) && (
            <div data-testid="claim-shift-customer-strip" className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-[#D4AF37] font-bold">Customer</p>
                <p className="text-white font-medium truncate">{appointment.customer_name || '—'}</p>
                {!appointment.customer_phone && (
                  <p data-testid="claim-shift-no-phone" className="text-red-400 text-xs italic mt-0.5">No phone number on file</p>
                )}
              </div>
              {appointment.customer_phone && (
                <a
                  href={`tel:${(appointment.customer_phone || '').replace(/[^+\d]/g, '')}`}
                  data-testid="claim-shift-call-link"
                  className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md bg-[#D4AF37] text-[#0A0A0A] font-bold hover:bg-[#F3E5AB] active:bg-[#D4AF37] text-sm flex-shrink-0"
                >
                  <Phone className="w-4 h-4" /> {appointment.customer_phone}
                </a>
              )}
            </div>
          )}
          <div>
            <p className="text-[#525252] text-[10px] uppercase tracking-wide mb-2">Estimated time of arrival</p>
            <div className="grid grid-cols-4 gap-2">
              {ETA_OPTIONS.map((mins) => (
                <button
                  key={mins}
                  type="button"
                  data-testid={`claim-eta-${mins}`}
                  onClick={() => setEta(mins)}
                  disabled={submitting}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors flex flex-col items-center gap-0.5 ${
                    eta === mins
                      ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                      : 'bg-[#0A0A0A] border-[#262626] text-white hover:border-[#D4AF37]/40'
                  }`}
                >
                  <Clock className="w-3.5 h-3.5" />
                  {mins} min
                </button>
              ))}
            </div>
          </div>

          {appointment.customer_id ? (
            <div className="space-y-1.5">
              <p className="text-[#525252] text-[10px] uppercase tracking-wide">Customer will receive</p>
              <div data-testid="claim-shift-preview" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-white text-sm leading-relaxed">
                {previewText}
              </div>
            </div>
          ) : (
            <p data-testid="claim-shift-no-customer" className="text-[#A3A3A3] text-xs">
              No customer on this shift — claiming will only update the status. No SMS will be sent.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {appointment.customer_id ? (
            <>
              <Button
                data-testid="claim-shift-send"
                onClick={() => submit(true)}
                disabled={submitting}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"
              >
                <Send className="w-4 h-4 mr-1.5" /> Send ETA Message
              </Button>
              <Button
                data-testid="claim-shift-skip"
                onClick={() => submit(false)}
                disabled={submitting}
                variant="outline"
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1f1f1f] hover:text-white flex-1"
              >
                <Slash className="w-4 h-4 mr-1.5" /> Skip
              </Button>
            </>
          ) : (
            <Button
              data-testid="claim-shift-confirm"
              onClick={() => submit(false)}
              disabled={submitting}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"
            >
              <Truck className="w-4 h-4 mr-1.5" /> Claim Shift
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimShiftDialog;
